import { ArrowRight, FileText, HardDrive, MemoryStick } from 'lucide-react';
import { useState } from 'react';

import { Button } from '../../button';
import { Input } from '../../input';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect as SelectField } from './learning-model-surface';
import {
  commitFrame,
  copyStoredState,
  emptyStoredState,
  identityFrame,
  interruptedWriteFrame,
  loadExampleOptions,
  loadingTrace,
  type ChangeOperation,
  type CopyMethod,
  type LoadExample,
  type StoredState,
} from './persistence-models';
import { normalizeTitle, validTitle } from './title-model';

import './persistence-labs.css';

function StateView({ state, label }: { state: StoredState | null; label: string }) {
  return (
    <div className="ps-state" role="group" aria-label={label}>
      {state ? (
        <>
          <div className="ps-counter">
            <span>Next ID</span>
            <strong>{state.next_id}</strong>
          </div>
          {state.tasks.length ? (
            <ul>
              {state.tasks.map((task) => (
                <li key={task.id}>
                  <span className="ps-id">#{task.id}</span>
                  <span>{task.title}</span>
                  <small>{task.completed ? 'Complete' : 'Open'}</small>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ps-empty">No tasks</p>
          )}
        </>
      ) : (
        <p className="ps-empty">No running process</p>
      )}
    </div>
  );
}
function Steps({ labels, step }: { labels: string[]; step: number }) {
  return (
    <ol className="ps-steps" aria-label="Experiment progress">
      {labels.map((label, index) => (
        <li
          key={label}
          aria-current={index === step ? 'step' : undefined}
          data-passed={index < step}
        >
          <span>{index + 1}</span>
          <strong>{label}</strong>
        </li>
      ))}
    </ol>
  );
}
function Prediction({ children }: { children: string }) {
  return (
    <p className="ps-prediction">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function FileContents({ label, text }: { label: string; text: string | null }) {
  return (
    <div className="ps-file" role="group" aria-label={label}>
      <span>
        <FileText size={14} aria-hidden="true" />
        {label}
      </span>
      <pre>{text === null ? 'No file' : text === '' ? '(empty file — zero bytes)' : text}</pre>
    </div>
  );
}

export function PersistenceLifetimeLab() {
  const initial = (): StoredState => ({
    next_id: 2,
    tasks: [{ id: 1, title: 'Read notes', completed: false }],
  });
  const [memory, setMemory] = useState<StoredState | null>(initial);
  const [saved, setSaved] = useState<StoredState | null>(null);
  const [title, setTitle] = useState('Call Alex');
  const [message, setMessage] = useState(
    'One task is in the running process. No saved file exists.',
  );
  return (
    <Lab
      title="What survives when the process stops?"
      eyebrow="Experiment 1 · lifetime"
      className="ps-lab"
    >
      <Prediction>
        After saving, stopping and starting, will the tasks appear in memory before you load them?
      </Prediction>
      <div className="ps-source">
        <FileText size={16} aria-hidden="true" />
        <span>
          <strong>Source code stays on disk</strong>
          <br />
          Its startup instruction creates an empty task list. Changing tasks does not rewrite this
          instruction.
        </span>
      </div>
      <div className="cb-two">
        <Panel label="Running process · temporary memory">
          <MemoryStick size={17} aria-hidden="true" />
          <StateView state={memory} label="Process memory" />
        </Panel>
        <Panel label="Saved file · outside the process">
          <HardDrive size={17} aria-hidden="true" />
          {saved ? (
            <StateView state={saved} label="Saved snapshot" />
          ) : (
            <p className="ps-empty">No saved file</p>
          )}
        </Panel>
      </div>
      <div className="cb-controls">
        <label>
          New task title
          <Input
            aria-label="New task title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>
        <Button
          variant="secondary"
          size="sm"
          disabled={!memory}
          onClick={() => {
            if (!memory) return;
            if (!validTitle(title)) {
              setMessage(
                'Use 1–80 valid Unicode characters after trimming spaces, tabs and line breaks. Memory and the file stay unchanged.',
              );
              return;
            }
            setMemory({
              next_id: memory.next_id + 1,
              tasks: [
                ...memory.tasks,
                { id: memory.next_id, title: normalizeTitle(title), completed: false },
              ],
            });
            setMessage('Only memory changed. The saved file still contains the earlier snapshot.');
          }}
        >
          Create in memory
        </Button>
      </div>
      <div className="cb-controls">
        <Button
          variant="secondary"
          size="sm"
          disabled={!memory}
          onClick={() => {
            if (memory) setSaved(copyStoredState(memory));
            setMessage(
              'A copy of memory is saved outside the process. Later edits will not change this copy.',
            );
          }}
        >
          Save snapshot
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            if (memory) {
              setMemory(null);
              setMessage(
                'The process stopped. Its memory is gone; source code and the saved file remain.',
              );
            } else {
              setMemory(emptyStoredState());
              setMessage(
                'A new process starts with an empty list. Starting did not load the saved file.',
              );
            }
          }}
        >
          {memory ? 'Stop process' : 'Start process'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!memory || !saved}
          onClick={() => {
            if (saved) setMemory(copyStoredState(saved));
            setMessage(
              'Loading replaces memory with the saved snapshot. Unsaved changes are not in that snapshot.',
            );
          }}
        >
          Load saved snapshot
        </Button>
        <Reset
          onClick={() => {
            setMemory(initial());
            setSaved(null);
            setTitle('Call Alex');
            setMessage('One task is in the running process. No saved file exists.');
          }}
        />
      </div>
      <Result>{message}</Result>
    </Lab>
  );
}

export function PersistencePathsLab() {
  const [directory, setDirectory] = useState('project');
  const [anchor, setAnchor] = useState('relative');
  const [mode, setMode] = useState('read');
  const [step, setStep] = useState(0);
  const workingDirectory = directory === 'project' ? '/workspace/project' : '/workspace';
  const resolved =
    anchor === 'script' ? '/workspace/project/tasks.txt' : `${workingDirectory}/tasks.txt`;
  const originalExists = resolved === '/workspace/project/tasks.txt';
  const failed = !originalExists && mode === 'read' && step > 0;
  const current =
    mode === 'write' && step > 0
      ? step === 1
        ? ''
        : 'Read notes\nCall Alex\n'
      : originalExists
        ? 'Read notes\n'
        : null;
  const descriptions = [
    'The path names a location. The program has not opened or read that location.',
    failed
      ? 'Open failed: no file exists at this path. A path string does not create a file in read mode.'
      : mode === 'write'
        ? 'Opening with w creates the file or empties its old contents immediately. Nothing has been written yet.'
        : 'The file is open in read mode. Its text has not been read into memory yet.',
    mode === 'write'
      ? 'write sends the new text to the open file. Notice which directory contains it.'
      : 'read brings text into memory. The saved file remains unchanged.',
    'The with block has ended and the file is closed. The file itself still exists.',
  ];
  const choose = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setStep(0);
  };
  return (
    <Lab
      title="A path is a location, not a promise"
      eyebrow="Experiment 2 · opening files"
      className="ps-lab"
    >
      <Prediction>
        Apollo from the parent directory. Will the relative path still reach the file beside the
        program?
      </Prediction>
      <div className="cb-controls">
        <label>
          Start the command in
          <SelectField
            aria-label="Starting directory"
            value={directory}
            onValueChange={choose(setDirectory)}
            options={[
              { value: 'project', label: '/workspace/project' },
              { value: 'parent', label: '/workspace' },
            ]}
          />
        </label>
        <label>
          Locate tasks.txt
          <SelectField
            aria-label="Path rule"
            value={anchor}
            onValueChange={choose(setAnchor)}
            options={[
              { value: 'relative', label: 'Relative to starting directory' },
              { value: 'script', label: 'Beside the program file' },
            ]}
          />
        </label>
        <label>
          Open mode
          <SelectField
            aria-label="Open mode"
            value={mode}
            onValueChange={choose(setMode)}
            options={[
              { value: 'read', label: 'r · read existing text' },
              { value: 'write', label: 'w · replace with new text' },
            ]}
          />
        </label>
      </div>
      <div className="ps-path">
        <code>program: /workspace/project/cli.py</code>
        <ArrowRight size={15} aria-hidden="true" />
        <code>resolved file: {resolved}</code>
      </div>
      <Steps
        labels={['Choose location', 'Open', mode === 'read' ? 'Read' : 'Write', 'Close']}
        step={step}
      />
      <div className="cb-two">
        <Panel label="Practice files">
          <FileContents
            label="/workspace/project/tasks.txt"
            text={originalExists ? current : 'Read notes\n'}
          />
          {!originalExists && <FileContents label="/workspace/tasks.txt" text={current} />}
        </Panel>
        <Panel label="Inside the running process">
          <pre>
            {failed
              ? 'FileNotFoundError'
              : step === 0
                ? 'No open file'
                : step === 3
                  ? 'File closed'
                  : `File open for ${mode === 'read' ? 'reading' : 'writing'}`}
          </pre>
          <p>
            {mode === 'read' && step >= 2 && !failed
              ? 'Text read into memory: Read notes'
              : 'No text has been read into memory.'}
          </p>
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={step === 3 || failed} onClick={() => setStep(step + 1)}>
          Next file operation
        </Button>
        <Reset
          onClick={() => {
            setDirectory('project');
            setAnchor('relative');
            setMode('read');
            setStep(0);
          }}
        />
      </div>
      <Result tone={failed ? 'warning' : 'neutral'}>{descriptions[step]}</Result>
      <p className="ps-caption">
        Changing a control restores both practice files and starts this experiment again.
      </p>
    </Lab>
  );
}

export function PersistenceRepresentationLab() {
  const [title, setTitle] = useState('Café review');
  const [completed, setCompleted] = useState(false);
  const [step, setStep] = useState(0);
  const value = { title, completed };
  const json = JSON.stringify(value);
  const bytes = new TextEncoder().encode(json);
  const python = `{"title": ${JSON.stringify(title)}, "completed": ${completed ? 'True' : 'False'}}`;
  const messages = [
    'The task currently exists as Python values in memory.',
    `Serialization produced JSON text in memory. Python ${completed ? 'True' : 'False'} becomes JSON ${completed ? 'true' : 'false'}. No bytes have been written to a file.`,
    `UTF-8 encodes the JSON text as ${bytes.length} bytes. Encoding still has not saved a file.`,
    `Decoding the bytes and parsing the JSON reconstructs values. The boolean is a boolean again, not the text "${completed ? 'true' : 'false'}".`,
  ];
  return (
    <Lab
      title="The same meaning in different representations"
      eyebrow="Experiment 3 · values, text and bytes"
      className="ps-lab"
    >
      <Prediction>
        Change Café to Cafe. Does the number of letters change? Does the number of UTF-8 bytes
        change?
      </Prediction>
      <div className="cb-controls">
        <label>
          Title to represent
          <Input
            aria-label="Title to represent"
            value={title}
            maxLength={40}
            onChange={(event) => {
              setTitle(event.target.value);
              setStep(0);
            }}
          />
        </label>
        <Button
          variant="secondary"
          size="sm"
          aria-pressed={completed}
          onClick={() => {
            setCompleted(!completed);
            setStep(0);
          }}
        >
          Completed: {completed ? 'True' : 'False'}
        </Button>
      </div>
      <Steps labels={['Python values', 'JSON text', 'UTF-8 bytes', 'Values again']} step={step} />
      <div className="ps-representations">
        <Panel label="1 · Values in memory">
          <pre>{python}</pre>
          <small>Python spelling: True / False</small>
        </Panel>
        <Panel label="2 · Serialized JSON text">
          <pre>{step >= 1 ? json : 'Serialize to reveal the text.'}</pre>
          <small>JSON spelling: true / false</small>
        </Panel>
        <Panel label="3 · Bytes representing that text">
          {step >= 2 ? (
            <>
              <div className="ps-bytes" aria-label="UTF-8 bytes">
                {Array.from(bytes).map((byte, index) => (
                  <code key={index}>{byte.toString(16).padStart(2, '0').toUpperCase()}</code>
                ))}
              </div>
              <small>
                {Array.from(json).length} Unicode code points · {bytes.length} bytes · hexadecimal
                shown
              </small>
              <div className="ps-character-bytes">
                {Array.from(title).map((character, index) => (
                  <span key={index}>
                    <strong>{character === ' ' ? 'space' : character}</strong>
                    <code>
                      {Array.from(new TextEncoder().encode(character))
                        .map((byte) => byte.toString(16).padStart(2, '0').toUpperCase())
                        .join(' ')}
                    </code>
                  </span>
                ))}
              </div>
            </>
          ) : (
            <p>Encode to reveal the bytes.</p>
          )}
        </Panel>
        <Panel label="4 · Reconstructed values">
          <pre>{step >= 3 ? python : 'Decode and parse to recover values.'}</pre>
          <small>
            {step >= 3
              ? `title: string · completed: boolean (${completed ? 'True' : 'False'})`
              : 'The destination expects values with their types.'}
          </small>
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={step === 3} onClick={() => setStep(step + 1)}>
          {
            ['Serialize to JSON', 'Encode as UTF-8', 'Decode and parse', 'Round trip complete'][
              step
            ]
          }
        </Button>
        <Reset
          onClick={() => {
            setTitle('Café review');
            setCompleted(false);
            setStep(0);
          }}
        />
      </div>
      <Result>{messages[step]}</Result>
      <p className="ps-caption">
        This models json.dumps with ensure_ascii=False. Every representation stays in this page’s
        memory; writing a file is a separate operation. Empty text is representable even though the
        task application would reject it as a title.
      </p>
      <p className="ps-caption">
        A Unicode code point identifies a text element such as a letter. Some visible symbols use
        several code points. Each byte above is written as two hexadecimal digits, using 0–9 and
        A–F: the letter é needs the two bytes C3 A9 in UTF-8.
      </p>
    </Lab>
  );
}

export function PersistenceSnapshotLab() {
  const [step, setStep] = useState(0);
  const [removeAll, setRemoveAll] = useState(false);
  const frame = identityFrame(step, removeAll);
  const descriptions = [
    'Both programs previously issued IDs 1, 2 and 3. The next unused ID is 4.',
    removeAll
      ? 'All records are deleted, but IDs 1–3 have already been issued. The next ID remains 4.'
      : 'ID 3 is deleted. next_id stays 4 because deletion must not make an old identity available again.',
    'Both designs save their chosen data. The records-only file omits the history carried by next_id.',
    `Both processes restart. Records-only guesses ${frame.recordsOnly.next_id}; the complete snapshot restores 4.`,
    `Records-only reuses ID ${frame.recordsOnly.tasks.at(-1)?.id}. The complete snapshot issues ID 4. Neither design saves this new task until another save happens.`,
  ];
  return (
    <Lab
      title="The missing history in a list of records"
      eyebrow="Experiment 4 · complete snapshots"
      className="ps-lab"
    >
      <Prediction>
        Once the highest ID is deleted, can the remaining records tell you that this ID was used
        before?
      </Prediction>
      <div className="cb-controls">
        <label>
          Deletion experiment
          <SelectField
            aria-label="Deletion experiment"
            value={removeAll ? 'all' : 'highest'}
            onValueChange={(value) => {
              setRemoveAll(value === 'all');
              setStep(0);
            }}
            options={[
              { value: 'highest', label: 'Delete the highest ID' },
              { value: 'all', label: 'Delete every task' },
            ]}
          />
        </label>
      </div>
      <Steps
        labels={['Initial state', 'Delete', 'Save', 'Restart and load', 'Create']}
        step={step}
      />
      <div className="cb-two">
        <Panel label="Save only task records">
          <StateView state={frame.recordsOnly} label="Records-only memory" />
          <FileContents label="records-only.json" text={frame.recordsFile} />
          {step >= 3 && (
            <p className="ps-caption">
              Guess next_id = largest remaining ID + 1, or 1 for an empty list.
            </p>
          )}
        </Panel>
        <Panel label="Save the complete state">
          <StateView state={frame.full} label="Complete-snapshot memory" />
          <FileContents label="complete-state.json" text={frame.fullFile} />
          {step >= 3 && <p className="ps-caption">Restore next_id exactly as it was saved.</p>}
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={step === 4} onClick={() => setStep(step + 1)}>
          {
            [
              'Delete',
              'Save both snapshots',
              'Restart and load both',
              'Create in both',
              'Experiment complete',
            ][step]
          }
        </Button>
        <Reset
          onClick={() => {
            setStep(0);
            setRemoveAll(false);
          }}
        />
      </div>
      <Result tone={step >= 3 ? 'warning' : 'neutral'}>{descriptions[step]}</Result>
    </Lab>
  );
}

export function PersistenceValidationLab() {
  const [example, setExample] = useState<LoadExample>('valid');
  const [step, setStep] = useState(0);
  const trace = loadingTrace(example);
  const current = trace.steps[step - 1];
  const ended = step === trace.steps.length;
  return (
    <Lab
      title="A readable file can still contain the wrong state"
      eyebrow="Experiment 5 · inspect before accepting"
      className="ps-lab"
    >
      <Prediction>
        Choose “Completion is a string”. Will JSON parsing reject it, or must the application
        inspect the values?
      </Prediction>
      <div className="cb-controls">
        <label>
          Stored file example
          <SelectField
            aria-label="Stored file example"
            value={example}
            onValueChange={(value) => {
              setExample(value as LoadExample);
              setStep(0);
            }}
            options={loadExampleOptions}
          />
        </label>
      </div>
      <div className="cb-two">
        <Panel label="Original file · never changed here">
          <pre className="ps-json">{trace.source}</pre>
        </Panel>
        <Panel label="Loading inspection">
          <ol className="ps-inspection">
            {trace.steps.map((entry, index) => (
              <li
                key={entry.title}
                data-outcome={index < step ? entry.outcome : 'waiting'}
                aria-current={index === step - 1 ? 'step' : undefined}
              >
                <span>
                  {index < step
                    ? entry.outcome === 'stop'
                      ? 'Stopped'
                      : entry.outcome === 'empty'
                        ? 'First run'
                        : 'Passed'
                    : 'Waiting'}
                </span>
                <strong>{entry.title}</strong>
                {index < step && <p>{entry.detail}</p>}
              </li>
            ))}
          </ol>
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={ended} onClick={() => setStep(step + 1)}>
          Inspect next stage
        </Button>
        <Reset
          onClick={() => {
            setExample('valid');
            setStep(0);
          }}
        />
      </div>
      <Result tone={current?.outcome === 'stop' ? 'warning' : ended ? 'success' : 'neutral'}>
        {current?.detail ?? 'No state is accepted yet. Start by trying to open the file.'}
      </Result>
      {ended && (
        <p className="ps-caption">
          {current?.outcome === 'stop'
            ? 'Startup stops. The original file is preserved so the problem can be investigated.'
            : current?.outcome === 'empty'
              ? 'Only a missing file follows the first-run policy. Other failures do not silently become an empty application.'
              : 'All required checks passed. Parsing alone would not have established these promises.'}
        </p>
      )}
    </Lab>
  );
}

export function PersistenceWriteLab() {
  const [step, setStep] = useState(0);
  const [failed, setFailed] = useState(false);
  const frame = interruptedWriteFrame(step);
  const messages = [
    'Both designs begin with the same accepted file. We want to change completed from false to true.',
    'Direct overwrite already emptied tasks.json. The temporary-file design has only emptied its separate temporary file.',
    'Only part of the new text has been written. Neither partial file is valid JSON.',
    'The new text is fully written and the writer is closed. Direct overwrite has changed the accepted file; the temporary design has not replaced it yet.',
    'Replacement switches the accepted path to the completed temporary file. The temporary name disappears.',
  ];
  return (
    <Lab
      title="Stop a save halfway through"
      eyebrow="Experiment 6 · protecting the previous snapshot"
      className="ps-lab"
      note="Write the temporary file first, then replace the saved snapshot. Compare what remains after a failure at each step."
    >
      <Prediction>
        Interrupt after the first chunk. Which design can still load the previous accepted snapshot?
      </Prediction>
      <Steps
        labels={[
          'Previous snapshot',
          'Open for writing',
          'Write a chunk',
          'Finish and close',
          'Replace',
        ]}
        step={step}
      />
      <div className="cb-two">
        <Panel label="Simple design · overwrite tasks.json">
          <FileContents label="Direct: tasks.json" text={frame.direct} />
          <p className="ps-caption">The old accepted file is also the write destination.</p>
        </Panel>
        <Panel label="Refined design · prepare another file">
          <FileContents label="Protected: tasks.json" text={frame.safe} />
          <FileContents label="Temporary: tasks.tmp" text={frame.temporary} />
          <p className="ps-caption">
            Both files are in the same directory. Replace only after the temporary write and close
            succeed.
          </p>
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={failed || step === 4} onClick={() => setStep(step + 1)}>
          Advance both saves
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={failed || step === 0 || step === 4}
          onClick={() => setFailed(true)}
        >
          Fail now, before replacement
        </Button>
        <Reset
          onClick={() => {
            setStep(0);
            setFailed(false);
          }}
        />
      </div>
      <Result tone={failed ? 'warning' : step === 4 ? 'success' : 'neutral'}>
        {failed
          ? `Saving stopped at “${['previous snapshot', 'open', 'partial write', 'finished write'][step]}”. The temporary file remains, but loading uses only tasks.json. The protected tasks.json still holds the previous snapshot. Direct overwrite ${step === 1 ? 'left an empty file' : step === 2 ? 'left an incomplete file' : 'already changed the file'}.`
          : messages[step]}
      </Result>
    </Lab>
  );
}

export function PersistenceCommitLab() {
  const [method, setMethod] = useState<CopyMethod>('deep');
  const [operation, setOperation] = useState<ChangeOperation>('complete');
  const [failSave, setFailSave] = useState(false);
  const [step, setStep] = useState(0);
  const frame = commitFrame(step, method, operation, failSave);
  const stopped = (step >= 1 && frame.rejected) || (step >= 2 && failSave);
  const leaked = JSON.stringify(frame.accepted) !== JSON.stringify(frame.previous) && step < 3;
  const descriptions = [
    'Current data in memory and the file agree. Make changes in a separate working copy before trying to save it.',
    frame.rejected
      ? operation === 'blank'
        ? 'The title is blank. Reject before changing any state or attempting a save.'
        : 'ID 99 does not exist. Reject before changing state or attempting a save.'
      : leaked
        ? 'Changing the working copy also changed the current data. They still share the same task records.'
        : 'Only the working copy changed. Current data in memory and the file still agree.',
    failSave
      ? leaked
        ? 'Saving failed before replacement. The file stayed unchanged, but current data in memory already changed. Memory and the file now disagree.'
        : 'Saving failed before replacement. Discard the working copy. Current data in memory and the file are both unchanged.'
      : 'The working copy is now saved in the file. The application has not reported success yet.',
    'Replace current data in memory with the saved working copy, then report success. Memory and the file agree again.',
  ];
  return (
    <Lab
      title="When is it honest to say Saved?"
      eyebrow="Experiment 7 · propose, save, accept"
      className="ps-lab"
    >
      <Prediction>
        Try “Copy the outer dictionary” with completion and a failed save. Does the nested task
        remain independent?
      </Prediction>
      <div className="cb-controls">
        <label>
          Prepare the change with
          <SelectField
            aria-label="Copy method"
            value={method}
            onValueChange={(value) => {
              setMethod(value as CopyMethod);
              setStep(0);
            }}
            options={[
              { value: 'direct', label: 'Change current data directly' },
              { value: 'shallow', label: 'Copy the outer dictionary only' },
              { value: 'deep', label: 'Copy all nested records' },
            ]}
          />
        </label>
        <label>
          Requested change
          <SelectField
            aria-label="Requested change"
            value={operation}
            onValueChange={(value) => {
              setOperation(value as ChangeOperation);
              setStep(0);
            }}
            options={[
              { value: 'create', label: 'Create “Café review”' },
              { value: 'complete', label: 'Complete task #1' },
              { value: 'delete', label: 'Delete task #1' },
              { value: 'blank', label: 'Create a blank title' },
              { value: 'missing', label: 'Complete missing task #99' },
            ]}
          />
        </label>
        <label>
          Save outcome
          <SelectField
            aria-label="Save outcome"
            value={failSave ? 'fail' : 'ok'}
            onValueChange={(value) => {
              setFailSave(value === 'fail');
              setStep(0);
            }}
            options={[
              { value: 'ok', label: 'Save succeeds' },
              { value: 'fail', label: 'Fail before replacement' },
            ]}
          />
        </label>
      </div>
      <Steps
        labels={['Current data', 'Prepare working copy', 'Save working copy', 'Use saved copy']}
        step={step}
      />
      <div className="ps-three">
        <Panel label="Current data in memory">
          <StateView state={frame.accepted} label="Current data in memory" />
        </Panel>
        <Panel label="Working copy · proposed change">
          {frame.candidate ? (
            <StateView state={frame.candidate} label="Working copy in memory" />
          ) : (
            <p className="ps-empty">No working copy prepared</p>
          )}
          {step >= 2 && failSave && (
            <p className="ps-caption">Discard this working copy after the failed save.</p>
          )}
        </Panel>
        <Panel label="Saved file">
          <StateView state={frame.disk} label="Saved file state" />
        </Panel>
      </div>
      {method !== 'deep' && step >= 1 && !frame.rejected && (
        <div className="ps-alias">
          <ArrowRight size={16} aria-hidden="true" />
          <span>
            {method === 'direct'
              ? 'There is no separate working copy: both names point to the same dictionary.'
              : 'Different outer dictionaries still point to the same tasks list and records.'}
          </span>
        </div>
      )}
      <div className="cb-controls">
        <Button size="sm" disabled={stopped || step === 3} onClick={() => setStep(step + 1)}>
          {
            [
              'Prepare change',
              'Try saving',
              'Use saved copy and report success',
              'Operation complete',
            ][step]
          }
        </Button>
        <Reset
          onClick={() => {
            setMethod('deep');
            setOperation('complete');
            setFailSave(false);
            setStep(0);
          }}
        />
      </div>
      <Result tone={stopped || leaked ? 'warning' : step === 3 ? 'success' : 'neutral'}>
        {descriptions[step]}
      </Result>
      <div className="ps-report" aria-label="Application response">
        <strong>Application response</strong>
        <span>
          {frame.rejected && step >= 1
            ? 'Request rejected. No save attempted.'
            : failSave && step >= 2
              ? 'Could not save. No success message.'
              : step === 3
                ? 'Saved.'
                : 'No success message yet.'}
        </span>
      </div>
      <p className="ps-caption">
        Each changed control restarts the same operation. The modeled save either succeeds or fails
        before replacing the file; concurrent processes and failure after replacement are outside
        this example.
      </p>
    </Lab>
  );
}
